import { openDB, type IDBPDatabase } from 'idb'

const DB_NAME = 'nest-offline'
const DB_VERSION = 1

interface NestDB {
  events: { key: string; value: any }
  'signed-urls': { key: string; value: { key: string; signedUrl: string; expiresAt: number } }
  'query-cache': { key: string; value: { buster: string; cache: string; timestamp: number } }
}

let dbPromise: Promise<IDBPDatabase<NestDB>> | null = null

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<NestDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('events')) {
          db.createObjectStore('events', { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains('signed-urls')) {
          db.createObjectStore('signed-urls', { keyPath: 'key' })
        }
        if (!db.objectStoreNames.contains('query-cache')) {
          db.createObjectStore('query-cache', { keyPath: 'buster' })
        }
      },
    })
  }
  return dbPromise
}

// ─── Events ──────────────────────────────────────────────────────

export async function saveEvents(events: any[]): Promise<void> {
  const db = await getDB()
  const tx = db.transaction('events', 'readwrite')
  await tx.store.clear()
  // Store metadata
  await tx.store.put({ id: '__meta', fetchedAt: new Date().toISOString(), eventCount: events.length })
  for (const event of events) {
    if (event.id) await tx.store.put(event)
  }
  await tx.done
}

export async function loadEvents(): Promise<{ events: any[]; fetchedAt: string | null }> {
  try {
    const db = await getDB()
    const all = await db.getAll('events')
    const meta = all.find((e: any) => e.id === '__meta')
    const events = all.filter((e: any) => e.id !== '__meta')
    return { events, fetchedAt: meta?.fetchedAt || null }
  } catch {
    return { events: [], fetchedAt: null }
  }
}

// ─── Signed URLs ────────────────────────────────────────────────

export async function saveSignedUrl(key: string, signedUrl: string, expiresIn: number): Promise<void> {
  const db = await getDB()
  await db.put('signed-urls', {
    key,
    signedUrl,
    expiresAt: Date.now() + expiresIn * 1000,
  })
}

export async function getSignedUrl(key: string): Promise<string | null> {
  try {
    const db = await getDB()
    const entry = await db.get('signed-urls', key)
    if (!entry) return null
    // Return only if >60s remaining
    if (entry.expiresAt > Date.now() + 60_000) return entry.signedUrl
    return null
  } catch {
    return null
  }
}

export async function cleanExpiredUrls(): Promise<void> {
  try {
    const db = await getDB()
    const tx = db.transaction('signed-urls', 'readwrite')
    const all = await tx.store.getAll()
    const now = Date.now()
    for (const entry of all) {
      if (entry.expiresAt < now) {
        await tx.store.delete(entry.key)
      }
    }
    await tx.done
  } catch { /* non-critical */ }
}

// ─── React Query Cache ──────────────────────────────────────────

export async function saveQueryCache(cache: string): Promise<void> {
  const db = await getDB()
  await db.put('query-cache', { buster: 'default', cache, timestamp: Date.now() })
}

export async function loadQueryCache(): Promise<string | null> {
  try {
    const db = await getDB()
    const entry = await db.get('query-cache', 'default')
    if (!entry) return null
    // Expire after 24 hours
    if (Date.now() - entry.timestamp > 24 * 60 * 60 * 1000) return null
    return entry.cache
  } catch {
    return null
  }
}
